import { useEffect } from 'react';
import { useApp } from '../context/AppContext';

export const useKeyboardShortcuts = () => {
  const { state, dispatch } = useApp();

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Don't handle shortcuts if user is typing in an input
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      const canEdit = !state.calculator.isLocked || state.calculator.lockedBy === state.currentUser.id;

      switch (event.key) {
        case 'Enter':
          event.preventDefault();
          if (canEdit && state.calculator.currentExpression.trim()) {
            // Calculate result
            const { CalculatorEngine } = require('../utils/calculator');
            const calculator = new CalculatorEngine();
            calculator.setVariables(state.variables);
            
            const { result, isError, errorMessage } = calculator.evaluate(state.calculator.currentExpression);
            
            if (isError) {
              dispatch({ type: 'SET_ERROR', payload: { isError: true, message: errorMessage } });
            } else {
              const calculation = {
                id: Date.now().toString(),
                expression: state.calculator.currentExpression,
                result,
                timestamp: new Date(),
                userId: state.currentUser.id,
                userName: state.currentUser.name,
                userColor: state.currentUser.color,
                isApproved: false,
                approvedBy: [],
                mode: state.calculator.mode,
              };
              dispatch({ type: 'ADD_CALCULATION', payload: calculation });
              dispatch({ type: 'SET_CURRENT_RESULT', payload: result });
            }
          }
          break;

        case 'Escape':
          event.preventDefault();
          if (canEdit) {
            dispatch({ type: 'SET_CURRENT_EXPRESSION', payload: '' });
            dispatch({ type: 'SET_CURRENT_RESULT', payload: '' });
            dispatch({ type: 'SET_ERROR', payload: { isError: false, message: '' } });
          }
          break;

        case 'Delete':
        case 'Backspace':
          event.preventDefault();
          if (canEdit) {
            const newExpression = state.calculator.currentExpression.slice(0, -1);
            dispatch({ type: 'SET_CURRENT_EXPRESSION', payload: newExpression });
            
            if (state.calculator.isError) {
              dispatch({ type: 'SET_ERROR', payload: { isError: false, message: '' } });
            }
          }
          break;

        case 'c':
        case 'C':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            if (canEdit) {
              dispatch({ type: 'SET_CURRENT_EXPRESSION', payload: '' });
              dispatch({ type: 'SET_CURRENT_RESULT', payload: '' });
              dispatch({ type: 'SET_ERROR', payload: { isError: false, message: '' } });
            }
          }
          break;

        case '1':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            dispatch({ type: 'SET_MODE', payload: 'basic' });
          }
          break;

        case '2':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            dispatch({ type: 'SET_MODE', payload: 'scientific' });
          }
          break;

        case '3':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            dispatch({ type: 'SET_MODE', payload: 'programmer' });
          }
          break;

        case 'm':
        case 'M':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            dispatch({ type: 'TOGGLE_MULTIPLAYER_MODE' });
          }
          break;

        case 'b':
        case 'B':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            dispatch({ type: 'TOGGLE_BOT_ENABLED' });
          }
          break;

        default:
          // Handle number and operator keys
          if (canEdit && !event.ctrlKey && !event.metaKey && !event.altKey) {
            const key = event.key;
            
            // Numbers
            if (/[0-9]/.test(key)) {
              event.preventDefault();
              dispatch({ type: 'SET_CURRENT_EXPRESSION', payload: state.calculator.currentExpression + key });
            }
            
            // Basic operators
            else if (['+', '-', '*', '/', '(', ')', '.'].includes(key)) {
              event.preventDefault();
              dispatch({ type: 'SET_CURRENT_EXPRESSION', payload: state.calculator.currentExpression + key });
            }
            
            // Special characters
            else if (key === '=') {
              event.preventDefault();
              // Same as Enter key
              if (state.calculator.currentExpression.trim()) {
                const { CalculatorEngine } = require('../utils/calculator');
                const calculator = new CalculatorEngine();
                calculator.setVariables(state.variables);
                
                const { result, isError, errorMessage } = calculator.evaluate(state.calculator.currentExpression);
                
                if (isError) {
                  dispatch({ type: 'SET_ERROR', payload: { isError: true, message: errorMessage } });
                } else {
                  const calculation = {
                    id: Date.now().toString(),
                    expression: state.calculator.currentExpression,
                    result,
                    timestamp: new Date(),
                    userId: state.currentUser.id,
                    userName: state.currentUser.name,
                    userColor: state.currentUser.color,
                    isApproved: false,
                    approvedBy: [],
                    mode: state.calculator.mode,
                  };
                  dispatch({ type: 'ADD_CALCULATION', payload: calculation });
                  dispatch({ type: 'SET_CURRENT_RESULT', payload: result });
                }
              }
            }
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyPress);

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [state, dispatch]);
};
